
import { 
  Box, 
  Switch, 
  Typography, 
  useTheme, 
  useMediaQuery,
  Paper, 
  FormControl, 
  TextField, 
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Stack
} from "@mui/material";

import QrCode2RoundedIcon from "@mui/icons-material/QrCode2Rounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import InboxIcon from "@mui/icons-material/Inbox";
import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported';
import SearchIcon from "@mui/icons-material/Search"; // Nuevo icono

import CustomSelect from "./selectFilterStyled"; // Asegúrate de que este path sea correcto
import CustomPagination from "./customPagination";
import { SendButton } from "./buttons";

function ListData({
  title,
  data,
  edit = false,
  editFunction = null,
  pages = 0,
  setPage = null,
  page = 1,
  changePage = null,
  create = false,
  createMessage = "Crear Nuevo",
  createFunction = null,
  createLimitMessage = "", // NUEVO: Mensaje a mostrar cuando se alcanza el límite
  pagination = true,
  nameColumn1 = "Nombre",
  nameColumn2 = "Subtítulo",
  nameColumn3 = "Acciones",
  
  // Props de Filtros
  filterSelect = false,
  filterSelectFunction = () => {},
  valuesFilter = [],
  valueFilter = "",
  
  // Props de Buscador (NUEVO)
  search = false,
  searchValue = "",
  searchFunction = () => {},
  searchPlaceholder = "Buscar...",

  qr_option = false,
  deleted = false,
  deletedFunction = () => {},
  dynamicColumns = [],
  showSwitch = false,
  onSwitchChange = null,
  extraActions = [],
}) {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("md"));

  const handleChange = (event, value) => {
    if (setPage) setPage(value);
    if (changePage) changePage(value);
  };

  const COLORS = {
    BG_MAIN: "#FFFFFF",
    BG_SUBTLE: "#F8FAFC",
    TEXT_PRIMARY: "#1E293B",
    TEXT_SECONDARY: "#64748B",
    BORDER: "#E2E8F0",
    HOVER_ROW: "#F1F5F9",
    INPUT_BG: "#F1F5F9", // Fondo gris suave para inputs
    EDIT_BG: "rgba(59, 130, 246, 0.1)",
    EDIT_TEXT: "#2563EB",
    DELETE_BG: "rgba(239, 68, 68, 0.1)",
    DELETE_TEXT: "#DC2626",
  };

  const styles = {
    container: {
      width: "100%",
      display: "flex",
      flexDirection: "column",
      gap: 3,
      alignItems: "center",
      padding: isSmall ? "10px" : "20px 0",
    },
    topBar: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      width: "100%",
      maxWidth: "1400px",
      flexWrap: "wrap",
      gap: 2,
      marginBottom: 2,
    },
    title: {
      color: COLORS.TEXT_PRIMARY,
      fontSize: isSmall ? "1.75rem" : "2rem",
      fontWeight: 800,
      marginRight: "auto", // Empuja los filtros a la derecha
    },
    // Contenedor unificado para buscador, filtros y botón
    actionsWrapper: {
      display: "flex",
      gap: 2,
      alignItems: "center",
      flexWrap: "wrap",
      flex: isSmall ? "1 1 100%" : "0 1 auto", // En móvil ocupa todo el ancho
    },
    searchInput: {
      backgroundColor: COLORS.INPUT_BG,
      borderRadius: "12px",
      "& .MuiOutlinedInput-root": {
        "& fieldset": { borderColor: "transparent" },
        "&:hover fieldset": { borderColor: "#CBD5E1" },
        "&.Mui-focused fieldset": { borderColor: theme.palette.primary.main },
      },
      minWidth: "220px",
    },
    tableContainer: {
      width: "100%",
      maxWidth: "1400px",
      backgroundColor: COLORS.BG_MAIN,
      borderRadius: "16px",
      boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
      border: `1px solid ${COLORS.BORDER}`,
      overflow: "hidden",
    },
    tableHeadCell: {
      backgroundColor: COLORS.BG_SUBTLE,
      color: COLORS.TEXT_SECONDARY,
      fontWeight: 700,
      textTransform: "uppercase",
      fontSize: "0.75rem",
      borderBottom: `1px solid ${COLORS.BORDER}`,
      padding: "16px",
    },
    tableBodyCell: {
      color: COLORS.TEXT_PRIMARY,
      borderBottom: `1px solid ${COLORS.BORDER}`,
      padding: "16px",
      fontSize: "0.95rem",
      verticalAlign: "middle",
    },
    imageBox: {
      width: "120px",
      height: "80px",
      borderRadius: "12px",
      overflow: "hidden",
      border: `1px solid ${COLORS.BORDER}`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: COLORS.BG_SUBTLE,
    },
    image: {
      width: "100%",
      height: "100%",
      objectFit: "cover",
    },
    actionBtnEdit: {
      backgroundColor: COLORS.EDIT_BG,
      color: COLORS.EDIT_TEXT,
      borderRadius: "8px",
      padding: "8px",
      "&:hover": { backgroundColor: "rgba(59, 130, 246, 0.2)" },
    },
    actionBtnDelete: {
      backgroundColor: COLORS.DELETE_BG,
      color: COLORS.DELETE_TEXT,
      borderRadius: "8px",
      padding: "8px",
      "&:hover": { backgroundColor: "rgba(239, 68, 68, 0.2)" },
    },
  };

  const renderEmptyState = () => (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 8, gap: 2, color: COLORS.TEXT_SECONDARY }}>
      <InboxIcon sx={{ fontSize: 60, opacity: 0.2 }} />
      <Typography variant="h6">No hay datos para mostrar</Typography>
    </Box>
  );

  // --- VISTA MÓVIL (Cards) ---
  const renderCardList = () => (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
      {data.length === 0 && <Paper elevation={0} sx={{ p: 4, textAlign: 'center' }}>{renderEmptyState()}</Paper>}
      {data.map((item, index) => (
        <Paper key={`card-${index}`} elevation={0} sx={{ p: 2, borderRadius: "16px", border: `1px solid ${COLORS.BORDER}`, display: "flex", flexDirection: "column", gap: 2, backgroundColor: COLORS.BG_MAIN }}>
          <Box sx={{ display: "flex", gap: 2 }}>
            {(() => {
                const imgCol = dynamicColumns.find((c) => c.type === "image");
                if (imgCol && item[imgCol.dataKey]) {
                  return (
                    <Box sx={{ width: 100, height: 100, borderRadius: "12px", overflow: "hidden", flexShrink: 0 }}>
                      <img src={item[imgCol.dataKey]} alt="img" style={styles.image} />
                    </Box>
                  );
                }
                return null;
            })()}
            <Box sx={{ flex: 1 }}>
               <Typography variant="h6" fontWeight={700} color={COLORS.TEXT_PRIMARY}>{item.title}</Typography>
               {item.title2 && <Typography variant="body2" color={COLORS.TEXT_SECONDARY}>{item.title2}</Typography>}
               <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                 {dynamicColumns.map((col, i) => {
                    if (col.type === 'image') return null;
                    return (
                      <Typography key={i} variant="caption" color="text.secondary" component="div">
                        <strong>{col.header}:</strong> {item[col.dataKey] ?? ""}
                      </Typography>
                    )
                 })}
               </Box>
            </Box>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, pt: 1, borderTop: `1px dashed ${COLORS.BORDER}` }}>
            {showSwitch && <Switch size="small" checked={!!item.is_active} onChange={(e) => onSwitchChange && onSwitchChange(e, item)} sx={{ mr: 'auto' }} />}
            {edit && <IconButton size="small" onClick={() => editFunction && editFunction(item.id, item.title, item.public_id, item)} sx={styles.actionBtnEdit}>{qr_option ? <QrCode2RoundedIcon fontSize="small" /> : <EditRoundedIcon fontSize="small" />}</IconButton>}
            {deleted && <IconButton size="small" onClick={() => deletedFunction && deletedFunction(item.id, item.title)} sx={styles.actionBtnDelete}><DeleteRoundedIcon fontSize="small" /></IconButton>}
          </Box>
        </Paper>
      ))}
    </Box>
  );

  // --- VISTA DESKTOP (Table) ---
  const renderTable = () => (
    <TableContainer component={Paper} sx={styles.tableContainer} elevation={0}>
      <Table sx={{ minWidth: 650 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={styles.tableHeadCell}>{nameColumn1}</TableCell>
            {nameColumn2 && <TableCell sx={styles.tableHeadCell}>{nameColumn2}</TableCell>}
            {dynamicColumns.map((col, i) => (
              <TableCell key={i} sx={{ ...styles.tableHeadCell, width: col.type === "image" ? "150px" : "auto" }}>{col.header}</TableCell>
            ))}
            <TableCell align="right" sx={styles.tableHeadCell}>{nameColumn3}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.length === 0 ? (
            <TableRow><TableCell colSpan={10} align="center">{renderEmptyState()}</TableCell></TableRow>
          ) : (
            data.map((item, index) => (
              <TableRow key={index} sx={{ "&:hover": { backgroundColor: COLORS.HOVER_ROW }, transition: "0.2s" }}>
                <TableCell sx={styles.tableBodyCell}><Typography variant="body1" fontWeight={600}>{item.title}</Typography></TableCell>
                {item.title2 && <TableCell sx={styles.tableBodyCell}><Typography variant="body2" color="text.secondary">{item.title2}</Typography></TableCell>}
                {dynamicColumns.map((column, colIndex) => {
                  if (column.type === "image") {
                    return (
                      <TableCell key={colIndex} sx={styles.tableBodyCell}>
                        <Box sx={styles.imageBox}>
                          {item[column.dataKey] ? <img src={item[column.dataKey]} alt="preview" style={styles.image} /> : <ImageNotSupportedIcon sx={{ color: COLORS.BORDER }} />}
                        </Box>
                      </TableCell>
                    );
                  }
                  return <TableCell key={colIndex} sx={styles.tableBodyCell}><Typography variant="body2" component="div">{item[column.dataKey] ?? ""}</Typography></TableCell>;
                })}
                <TableCell align="right" sx={styles.tableBodyCell}>
                  <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                    {showSwitch && <Switch size="small" checked={!!item.is_active} onChange={(e) => onSwitchChange && onSwitchChange(e, item)} />}
                    {extraActions.map((action, idx) => {
                         const shouldShow = action.show ? action.show(item) : true;
                         if (!shouldShow) return null;
                         const isDisabled = action.disabled ? action.disabled(item) : false;
                         return (
                            <Tooltip key={idx} title={isDisabled ? (action.disabledTooltip || "") : (action.tooltip || "")}>
                                <span>
                                  <IconButton 
                                    size="small" 
                                    onClick={() => action.onClick(item)} 
                                    disabled={isDisabled}
                                    sx={{ color: COLORS.TEXT_SECONDARY, bgcolor: COLORS.BG_SUBTLE, borderRadius: '8px', padding: '8px' }}
                                  >
                                      {action.icon}
                                  </IconButton>
                                </span>
                            </Tooltip>
                         )
                    })}
                    {edit && <Tooltip title="Editar"><IconButton size="small" onClick={() => editFunction && editFunction(item.id, item.title, item.public_id, item)} sx={styles.actionBtnEdit}>{qr_option ? <QrCode2RoundedIcon fontSize="small" /> : <EditRoundedIcon fontSize="small" />}</IconButton></Tooltip>}
                    {deleted && <Tooltip title="Eliminar"><IconButton size="small" onClick={() => deletedFunction && deletedFunction(item.id, item.title)} sx={styles.actionBtnDelete}><DeleteRoundedIcon fontSize="small" /></IconButton></Tooltip>}
                  </Stack>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box sx={styles.container}>
      {/* Top Bar Unificado */}
      <Box sx={styles.topBar}>
        <Typography sx={styles.title}>{title}</Typography>
        
        <Box sx={styles.actionsWrapper}>
          {/* 1. Buscador */}
          {search && (
            <TextField
              size="small"
              placeholder={searchPlaceholder}
              value={searchValue}
              onChange={searchFunction}
              sx={styles.searchInput}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: COLORS.TEXT_SECONDARY }} />
                  </InputAdornment>
                ),
              }}
            />
          )}

          {/* 2. Filtro Select */}
          {filterSelect && (
            <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
              <CustomSelect 
                value={valueFilter} 
                onChange={filterSelectFunction} 
                valuesFilter={valuesFilter} 
                // Asegúrate que tu CustomSelect maneje el placeholder o label por defecto
              />
            </FormControl>
          )}

          {/* 3. Botón Crear */}
          {create && !createLimitMessage && (
            <SendButton onClick={createFunction} sx={{ height: 40, px: 3, borderRadius: "10px", boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)" }}>
              {createMessage}
            </SendButton>
          )}

          {/* Mensaje de límite alcanzado en lugar del botón crear */}
          {createLimitMessage && (
            <Box sx={{ display: 'flex', alignItems: 'center', backgroundColor: '#fef2f2', padding: '8px 16px', borderRadius: '10px', border: '1px solid #fecaca' }}>
              <Typography variant="body2" sx={{ color: '#ef4444', fontWeight: 700, fontFamily: 'Como, sans-serif' }}>
                {createLimitMessage}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Contenido */}
      <Box sx={{ width: "100%", maxWidth: "1400px" }}>
        {isSmall ? renderCardList() : renderTable()}
      </Box>

      {/* Paginación */}
      {pagination && (
        <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
          <CustomPagination page={page} count={pages} onChange={handleChange} />
        </Box>
      )}
    </Box>
  );
}

export default ListData;